import React, { useState } from 'react';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import type { AuthorDoc } from '../../services/AuthorFirestoreService';

export interface AuthorSelectorProps {
  authors: AuthorDoc[];
  activeAuthorId: string | null;
  isOverview: boolean;
  canManageAuthors?: boolean;
  onSelect: (authorId: string) => void;
  onSetDefault: (authorId: string) => Promise<void>;
  onClone: (author: AuthorDoc) => void;
  onDelete: (author: AuthorDoc) => void;
}

export const AuthorSelector: React.FC<AuthorSelectorProps> = ({
  authors,
  activeAuthorId,
  isOverview,
  canManageAuthors = false,
  onSelect,
  onSetDefault,
  onClone,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [busyDefault, setBusyDefault] = useState<string | null>(null);
  const activeAuthor = authors.find((a) => a.id === activeAuthorId) ?? null;
  const label = isOverview ? 'All authors' : activeAuthor?.displayName ?? 'Choose author';

  const close = () => setAnchorEl(null);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={isOverview ? <GroupsIcon /> : <PersonIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="content-plan-author-selector"
      >
        {label}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem
          selected={isOverview}
          onClick={() => {
            onSelect('all');
            close();
          }}
          data-testid="author-selector-all"
        >
          <ListItemIcon><GroupsIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="All authors overview" secondary="Read-only" />
          {isOverview && <CheckIcon fontSize="small" />}
        </MenuItem>
        <Divider />
        {authors.map((author) => (
          <MenuItem
            key={author.id}
            selected={!isOverview && author.id === activeAuthorId}
            onClick={() => {
              if (author.id) onSelect(author.id);
              close();
            }}
            data-testid={`author-selector-item-${author.id}`}
          >
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <span>{author.displayName ?? author.id}</span>
                  {author.isDefault && <Chip size="small" label="Default" />}
                </Stack>
              }
              secondary={author.positioning ?? undefined}
            />
            <Stack direction="row" spacing={0.5} alignItems="center">
              {!author.isDefault && author.id && (
                <Tooltip title="Set as default baseline">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busyDefault === author.id}
                      onClick={async (event) => {
                        event.stopPropagation();
                        setBusyDefault(author.id ?? null);
                        try {
                          await onSetDefault(author.id!);
                        } finally {
                          setBusyDefault(null);
                        }
                      }}
                      data-testid={`author-selector-default-${author.id}`}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {canManageAuthors && author.id && (
                <Tooltip title="Clone author">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onClone(author);
                      close();
                    }}
                    data-testid={`author-selector-clone-${author.id}`}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canManageAuthors && author.id && (
                <Tooltip title={
                  authors.length <= 1
                    ? 'At least one author is required'
                    : author.isDefault
                      ? 'Set another default before deleting'
                      : 'Delete author'
                }>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={authors.length <= 1 || Boolean(author.isDefault)}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(author);
                        close();
                      }}
                      data-testid={`author-selector-delete-${author.id}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!isOverview && author.id === activeAuthorId && <CheckIcon fontSize="small" />}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
